import { createApi, fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";
import Axios from "axios";
import { config } from "../../../config";
import {
  getTransformedDataForPagination,
  LocalStorageKeys,
  objectToQueryParams,
} from "../../../utils";

export const workbookAPI = createApi({
  reducerPath: "workbookAPI",
  baseQuery: retry(
    fetchBaseQuery({
      baseUrl: config.api_url,
      prepareHeaders: (headers) => {
        const token = localStorage.getItem(LocalStorageKeys.authToken);

        // If we have a token set in state, let's assume that we should be passing it.
        if (token) {
          headers.set("authorization", `Bearer ${token}`);
          headers.set("Content-Type", "application/json");
        }

        return headers;
      },
    }),
    { maxRetries: 2 }
  ),
  tagTypes: ["Workbooks", "Workbook Items"],
  endpoints: (builder) => ({
    // Get All Workbooks
    getAllWorkbooks: builder.query({
      query: ({
        page = 0,
        pageSize = 10,
        status = null,
        type = null,
        object_type = null,
        object_id = null,
        sort_by = null,
        sort_order = null
      }) => ({
        url: `/workbook${page !== null ? `?page_no=${page + 1}` : ""}${pageSize ? `&page_size=${pageSize}` : ""
          }${status ? `&status=${status}` : ""}${type ? `&type=${type}` : ""}${object_type ? `&object_type=${object_type}` : ""
          }${object_id ? `&object_id=${object_id}` : ""}${sort_by ? `&sort_by=${sort_by}` : ''
          }${sort_order ? `&sort_order=${sort_order}` : ''}`,
        method: "GET",
      }),
      providesTags: ["Workbooks"],
      transformResponse: (
        data: any,
        meta: { request: Request; response?: Response }
      ) => {
        return getTransformedDataForPagination(data, meta?.response);
      },
    }),

    // Get Workbook by id
    getWorkbookById: builder.query({
      query: ({ id }) => ({
        url: `/workbook/${id}`,
        method: "GET",
      }),
    }),

    // Create Workbook
    createWorkbook: builder.mutation({
      query: (payload): Record<string, any> => ({
        url: "/workbook",
        method: "POST",
        body: payload,
      }),
    }),

    // Update Workbook
    updateWorkbook: builder.mutation({
      query: ({ id, payload }) => ({
        url: `/workbook/${id}`,
        method: "PUT",
        body: payload,
      }),
    }),

    // Import materials and boms based on csv file
    importWorkbook: builder.mutation({
      async queryFn(_arg, _queryApi, _extraOptions) {
        const { payload, id } = _arg;
        let result: any = {};
        try {
          const resData = await Axios({
            url: `${config.api_url}workbook/${id}/import`,
            method: "post",
            headers: {
              authorization: `Bearer ${localStorage.getItem(
                LocalStorageKeys.authToken
              )}`,
              "Content-Type": payload.type,
            },
            data: payload,
          });
          result = { data: resData?.data };
        } catch (err) {
          result = { error: true };
        } finally {
          return result;
        }
      },
    }),

    // Create Material and bom from workbook items
    batchCreateWorkbook: builder.mutation({
      query: ({ id, validation_only = false }): Record<string, any> => ({
        url: `/workbook/${id}?validation_only=${validation_only}`,
        method: "POST",
      }),
    }),

    // Delete Workbook
    deleteWorkbook: builder.mutation({
      query: ({ id }) => ({
        url: `/workbook/${id}`,
        method: "DELETE",
      }),
    }),

    // Workbook items API's

    // Get All items
    getAllWorkbookItems: builder.query({
      query: ({ page = 0, pageSize = 10, id, sort_by = null, sort_order = null }) => ({
        url: `/workbook/${id}/items${page !== null ? `?page_no=${page + 1}` : ""
          }${pageSize ? `&page_size=${pageSize}` : ""}${sort_by ? `&sort_by=${sort_by}` : ''
          }${sort_order ? `&sort_order=${sort_order}` : ''}`,
        method: "GET",
      }),
      providesTags: ["Workbook Items"],
      transformResponse: (
        data: any,
        meta: { request: Request; response?: Response }
      ) => {
        return getTransformedDataForPagination(data, meta?.response);
      },
    }),

    // Create Workbook item
    createWorkbookItem: builder.mutation({
      query: ({ id, payload }): Record<string, any> => ({
        url: `/workbook/${id}/items`,
        method: "POST",
        body: payload,
      }),
    }),

    // Update Workbook item
    updateWorkbookItem: builder.mutation({
      query: ({ id, item_id, payload }) => ({
        url: `/workbook/${id}/${item_id}`,
        method: "PUT",
        body: payload,
      }),
    }),

    // Delete Workbook item
    deleteWorkbookItem: builder.mutation({
      query: ({ id, item_id }) => ({
        url: `/workbook/${id}/${item_id}`,
        method: "DELETE",
      }),
    }),

    // Bulk Update Workbook items
    bulkUpdateWorkbookItems: builder.mutation({
      query: ({ id, payload }): Record<string, any> => ({
        url: `/workbook/${id}/items`,
        method: "PUT",
        body: payload,
      }),
    }),

    // Bulk Delete Workbook items
    bulkDeleteWorkbookItems: builder.mutation({
      query: ({ id, item_ids }) => ({
        url: `/workbook/${id}/items`,
        method: "DELETE",
        body: item_ids,
      }),
    }),

    // Get Workbook processing status
    getWorkbookProcessingStatus: builder.query({
      query: (id) => ({
        url: `/workbook/${id}/processing_status`,
        method: "GET",
      }),
    }),

    // Auto Populate
    autoPopulateWorkbookItem: builder.mutation({
      query: ({ id, group, region }): any => ({
        url: `/workbook/${id}/auto_populate${objectToQueryParams({ object_id: group ? group.value : region.value, object_type: group ? "group" : "uom" })}`,
        method: "PUT",
      }),
    }),
  }),
});

export const {
  useLazyGetAllWorkbooksQuery,
  useLazyGetWorkbookByIdQuery,
  useCreateWorkbookMutation,
  useUpdateWorkbookMutation,
  useDeleteWorkbookMutation,
  useImportWorkbookMutation,
  useBatchCreateWorkbookMutation,
  useLazyGetAllWorkbookItemsQuery,
  useCreateWorkbookItemMutation,
  useUpdateWorkbookItemMutation,
  useDeleteWorkbookItemMutation,
  useBulkDeleteWorkbookItemsMutation,
  useGetWorkbookProcessingStatusQuery,
  useLazyGetWorkbookProcessingStatusQuery,
  useAutoPopulateWorkbookItemMutation,
  useBulkUpdateWorkbookItemsMutation
} = workbookAPI;
